import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  message,
  Card,
  Tag,
  Tooltip,
  Row,
  Col,
  Typography,
  Alert,
  Empty,
  Badge,
} from 'antd';
import {
  InfoCircleOutlined,
  SettingOutlined,
  PlusOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import RuleConfigModal, { RuleConfigMode, RuleConfigItem } from '../components/RuleConfigModal';
import { ruleService, type RuleListItem } from '../services/rule';
import { usePermission } from '../hooks/usePermission';
import { RULE_TYPES, getRuleTypeInfo } from '../constants';
import { handleApiError } from '../utils/errorHandler';
import { useAuthStore } from '../stores/authStore';

const { Text, Title } = Typography;

const RuleManagement: React.FC = () => {
  const [ruleRows, setRuleRows] = useState<RuleListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [creatingNew, setCreatingNew] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<number | null>(null);
  const [searchText, setSearchText] = useState('');
  const [ruleIdFilter, setRuleIdFilter] = useState<string>(''); // 规则ID筛选
  const [clientIdFilter, setClientIdFilter] = useState<string>(''); // 客户ID筛选
  
  // 分页状态
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  
  // 临时输入状态（不触发自动搜索）
  const [searchInput, setSearchInput] = useState('');
  const [ruleIdInput, setRuleIdInput] = useState<string>('');
  const [clientIdInput, setClientIdInput] = useState<string>('');
  const [rules, setRules] = useState<RuleConfigItem[]>([]);
  const [ruleConfigModalVisible, setRuleConfigModalVisible] = useState(false);
  const [ruleConfigMode, setRuleConfigMode] = useState<RuleConfigMode>('create');

  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.user_type === 'admin';

  // 移除前端过滤，改用后端搜索
  
  // 权限检查
  const canUpdateRule = usePermission('rule:update');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async (overrideParams?: { search?: string; ruleId?: string; clientId?: string; page?: number; limit?: number }) => {
    setLoading(true);
    try {
      // 使用传入的分页参数或当前状态
      const page = overrideParams?.page ?? pagination.page;
      const limit = overrideParams?.limit ?? pagination.limit;
      const params: any = { page, limit };
      
      // 使用传入的参数或状态值
      const searchValue = overrideParams?.search !== undefined ? overrideParams.search : searchText;
      const ruleIdValue = overrideParams?.ruleId !== undefined ? overrideParams.ruleId : ruleIdFilter;
      const clientIdValue = overrideParams?.clientId !== undefined ? overrideParams.clientId : clientIdFilter;
      
      // 添加搜索条件
      if (searchValue) {
        params.search = searchValue;
      }
      if (ruleIdValue) {
        const ruleId = parseInt(ruleIdValue);
        if (!isNaN(ruleId) && ruleId > 0) {
          params.rule_id = ruleId;
        }
      }
      if (clientIdValue) {
        const clientId = parseInt(clientIdValue);
        if (!isNaN(clientId) && clientId > 0) {
          params.client_id = clientId;
        }
      }
      
      console.log('🔍 规则搜索参数:', params);
      
      const res = await ruleService.listAllRules(params);
      setRuleRows(
        res.list.map((rule) => ({
          ...rule,
          type: 'aggregated',
          name: rule.name || `规则${rule.id}`,
        }))
      );
      // 更新分页状态
      setPagination(prev => ({ ...prev, page, total: res.total }));
    } catch (error) {
      message.error('加载数据失败');
      console.error('加载数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRule = () => {
    setCreatingNew(true);
    setEditingRuleId(null);
    setRuleConfigMode('create');
    const initial = RULE_TYPES.map(r => ({ 
      id: Math.random(),
      type: r.type,
      name: r.name,
      isActive: false,
      config: {}
    }));
    setRules(initial);
    setRuleConfigModalVisible(true);
  };

  const handleEditRules = async (row: RuleListItem) => {
    setCreatingNew(false);
    setEditingRuleId(row.id);
    setRuleConfigMode('edit');
    try {
      const resp = await ruleService.getRuleDetail(row.id);
      const ruleConfig = (resp && (resp as any).rule_config) || {};
      const initial = RULE_TYPES.map(r => {
        const cfg = ruleConfig[r.type] || {};
        return { 
          id: Math.random(),
          type: r.type,
          name: r.name,
          config: cfg
        };
      });
      setRules(initial);
    } catch (e) {
      const initial = RULE_TYPES.map(r => ({ 
        id: Math.random(),
        type: r.type,
        name: r.name,
        config: {}
      }));
      setRules(initial);
    } finally {
      setRuleConfigModalVisible(true);
    }
  };

  const handleCopyRule = (row: RuleListItem) => {
    Modal.confirm({
      title: '确认复制规则',
      content: `确定要复制规则"${row.name || `规则${row.id}`}"吗？`,
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        setLoading(true);
        try {
          const resp = await ruleService.getRuleDetail(row.id);
          const ruleConfig = (resp && (resp as any).rule_config) || {};
          const copyName = `${row.name || `规则${row.id}`} (副本)`;
          
          await ruleService.createRule({
            name: copyName,
            rule_config: ruleConfig
          });
          
          message.success('规则复制成功');
          loadData();
        } catch (error) {
          console.error('复制规则失败:', error);
          handleApiError(error, '复制失败');
        } finally {
          setLoading(false);
        }
      }
    });
  };


  // 检查规则是否有效配置
  const checkRuleHasValidConfig = (ruleType: string, config: any): boolean => {
    if (!config || typeof config !== 'object') return false;

    switch (ruleType) {
      case 'url_referer':
        return (config.refererList && config.refererList.length > 0);

      case 'ip_blacklist':
        return (config.blacklist && config.blacklist.length > 0) ||
               (config.whitelist && config.whitelist.length > 0) ||
               (config.allowedIPs && config.allowedIPs.length > 0) ||
               (config.blockedIPs && config.blockedIPs.length > 0);

      case 'crawler':
        return (config.crawlerAllowed && config.crawlerAllowed.length > 0) ||
               (config.crawlerBlocked && config.crawlerBlocked.length > 0) ||
               config.searchEngineCrawler === true ||
               config.blockMaliciousCrawler === true;

      case 'geography':
        return (config.allowedCountries && config.allowedCountries.length > 0) ||
               (config.blockedCountries && config.blockedCountries.length > 0);

      case 'network':
        return config.proxyDetection === true ||
               config.datacenterDetection === true ||
               config.vpnDetection === true ||
               config.satelliteDetection === true ||
               config.mobileNetworkDetection === true ||
               config.icloudProxyDetection === true ||
               (config.blockedNetworks && config.blockedNetworks.length > 0) ||
               (config.allowedNetworks && config.allowedNetworks.length > 0) ||
               (config.riskLevels && config.riskLevels.length > 0);

      case 'device':
        const deviceConfigKeys = [
          'deviceTypeAllow', 'deviceTypeBlock', 'browserAllow', 'browserBlock',
          'clientTypeAllow', 'clientTypeBlock', 'mobileAppAllow', 'mobileAppBlock',
          'systemAllow', 'systemBlock', 'brandAllow', 'brandBlock'
        ];
        return deviceConfigKeys.some(key =>
          config[key] && Array.isArray(config[key]) && config[key].length > 0
        );

      case 'language':
        return (config.allowedLanguages && config.allowedLanguages.length > 0) ||
               (config.blockedLanguages && config.blockedLanguages.length > 0);

      case 'utm':
        return (config.utmSourceAllowed && config.utmSourceAllowed.length > 0) ||
               (config.utmSourceBlocked && config.utmSourceBlocked.length > 0) ||
               (config.utmMediumAllowed && config.utmMediumAllowed.length > 0) ||
               (config.utmMediumBlocked && config.utmMediumBlocked.length > 0) ||
               (config.utmCampaignAllowed && config.utmCampaignAllowed.length > 0) ||
               (config.utmCampaignBlocked && config.utmCampaignBlocked.length > 0) ||
               (config.utmTermAllowed && config.utmTermAllowed.length > 0) ||
               (config.utmTermBlocked && config.utmTermBlocked.length > 0) ||
               (config.utmContentAllowed && config.utmContentAllowed.length > 0) ||
               (config.utmContentBlocked && config.utmContentBlocked.length > 0);

      case 'advertising':
        return Array.isArray(config.requiredPlatforms) && config.requiredPlatforms.length > 0;

      default:
        return false;
    }
  };

  // 获取规则类型的中文名称和图标（使用统一的常量函数）
  const getRuleTypeDisplayInfo = (ruleType: string) => {
    return getRuleTypeInfo(ruleType);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      render: (id: number) => (
        <div style={{
          fontSize: '12px',
          fontFamily: 'monospace',
          fontWeight: 600,
          color: 'var(--color-text-secondary)',
          background: 'var(--color-bg-secondary)',
          padding: '4px 8px',
          borderRadius: '6px',
          display: 'inline-block'
        }}>
          {id}
        </div>
      ),
    },
    ...(isAdmin
      ? [
          {
            title: '用户ID',
            dataIndex: 'user_id',
            key: 'user_id',
            width: 90,
            render: (value: number | undefined) => (
              <Text
                style={{
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  color: 'var(--color-text-secondary)',
                }}
              >
                {value ?? '-'}
              </Text>
            ),
          },
          {
            title: '客户ID',
            dataIndex: 'client_id',
            key: 'client_id',
            width: 90,
            render: (value: number | undefined) => (
              <Text
                style={{
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  color: 'var(--color-text-secondary)',
                }}
              >
                {value ?? '-'}
              </Text>
            ),
          },
        ]
      : []),
    {
      title: '规则名称',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (name: string, record: RuleListItem) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '8px',
            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid rgba(99, 102, 241, 0.2)'
          }}>
            <SettingOutlined style={{ color: 'var(--color-primary)', fontSize: '16px' }} />
          </div>
          <div>
            <div style={{
              fontWeight: 600,
              color: 'var(--color-text-primary)',
              fontSize: '14px',
              letterSpacing: '-0.2px'
            }}>
              {name || `规则 #${record.id}`}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: '规则配置',
      dataIndex: 'rule_config',
      key: 'rule_config',
      render: (ruleConfig: any, record: any) => {
        // 统计启用的子规则
        const activeRules: string[] = [];
        if (ruleConfig && typeof ruleConfig === 'object') {
          Object.entries(ruleConfig).forEach(([ruleType, config]: [string, any]) => {
            if (checkRuleHasValidConfig(ruleType, config)) {
              activeRules.push(ruleType);
            }
          });
        }

        if (activeRules.length === 0) {
          return (
            <Tag
              icon={<InfoCircleOutlined />}
              color="default"
              style={{
                borderRadius: '6px',
                padding: '4px 12px',
                fontSize: '11px',
                background: 'var(--color-bg-secondary)',
                color: 'var(--color-text-tertiary)',
                border: '1px solid var(--color-border-light)'
              }}
            >
              未配置子规则
            </Tag>
          );
        }

        // 显示启用的规则类型
        return (
          <Space size={[6, 6]} wrap>
            <Badge
              count={activeRules.length}
              style={{
                backgroundColor: 'var(--color-success)',
                boxShadow: '0 2px 4px rgba(16, 185, 129, 0.2)',
                fontWeight: 600
              }}
            >
              <Tag
                color="success"
                style={{
                  margin: 0,
                  borderRadius: '6px',
                  fontWeight: 600,
                  padding: '4px 12px',
                  fontSize: '11px',
                  background: 'rgba(16, 185, 129, 0.1)',
                  color: 'var(--color-success)',
                  border: '1px solid rgba(16, 185, 129, 0.2)'
                }}
              >
                已配置
              </Tag>
            </Badge>
            {activeRules.slice(0, 3).map(ruleType => {
              const info = getRuleTypeDisplayInfo(ruleType);
              return (
                <Tooltip key={ruleType} title={info.name}>
                  <Tag
                    style={{
                      margin: 0,
                      borderRadius: '6px',
                      fontSize: '11px',
                      padding: '4px 10px',
                      background: 'rgba(99, 102, 241, 0.05)',
                      color: 'var(--color-text-secondary)',
                      border: '1px solid var(--color-border-light)',
                      fontWeight: 500
                    }}
                  >
                    {info.icon} {info.name}
                  </Tag>
                </Tooltip>
              );
            })}
            {activeRules.length > 3 && (
              <Tooltip title={activeRules.slice(3).map(r => getRuleTypeDisplayInfo(r).name).join(', ')}>
                <Tag style={{
                  margin: 0,
                  borderRadius: '6px',
                  background: 'var(--color-bg-secondary)',
                  color: 'var(--color-text-secondary)',
                  border: '1px solid var(--color-border-light)',
                  fontWeight: 500,
                  fontSize: '11px',
                  padding: '4px 10px'
                }}>
                  +{activeRules.length - 3}
                </Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (createdAt: string) => {
        return <span>{new Date(createdAt).toLocaleString()}</span>;
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      render: (_: any, record: RuleListItem) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<SettingOutlined />}
            onClick={() => handleEditRules(record)}
            style={{
              borderRadius: '6px',
              fontWeight: 500,
              padding: '4px 16px',
              height: '32px',
              background: 'var(--color-primary)',
              border: 'none',
              boxShadow: '0 2px 4px rgba(99, 102, 241, 0.2)',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(99, 102, 241, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(99, 102, 241, 0.2)';
            }}
          >
            编辑
          </Button>
          <Tooltip title="复制规则">
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={() => handleCopyRule(record)}
              style={{
                borderRadius: '6px',
                fontWeight: 500,
                padding: '4px 12px',
                height: '32px',
                border: '1px solid var(--color-border)',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.borderColor = 'var(--color-primary)';
                e.currentTarget.style.color = 'var(--color-primary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = 'var(--color-border)';
                e.currentTarget.style.color = '';
              }}
            >
              复制
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px', background: 'var(--color-bg-tertiary)', minHeight: 'calc(100vh - 64px)' }}>
      <div style={{
        background: 'var(--color-bg-primary)',
        padding: '24px 32px',
        borderRadius: 'var(--border-radius-lg)',
        marginBottom: '24px',
        boxShadow: 'var(--box-shadow-sm)',
        border: '1px solid var(--color-border-light)'
      }}>
        <Title level={2} style={{ margin: 0, marginBottom: '8px', fontSize: '28px', fontWeight: 700 }}>规则管理</Title>
        <Text type="secondary" style={{ fontSize: '14px' }}>配置和管理访客拦截规则</Text>
      </div>

      <Card
        style={{
          borderRadius: 'var(--border-radius-lg)',
          boxShadow: 'var(--box-shadow-sm)',
          transition: 'var(--transition-smooth)',
          border: '1px solid var(--color-border-light)'
        }}
        bodyStyle={{ padding: '24px' }}
      >
        <div style={{
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '16px',
          flexWrap: 'wrap'
        }}>
          <Space size="middle" wrap>
            <Input
              placeholder="搜索规则名称或描述"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onPressEnter={() => {
                setSearchText(searchInput);
                setRuleIdFilter(ruleIdInput);
                setClientIdFilter(clientIdInput);
                loadData({ search: searchInput, ruleId: ruleIdInput, clientId: clientIdInput });
              }}
              allowClear
              onClear={() => {
                setSearchInput('');
                setSearchText('');
              }}
              style={{
                width: 200,
                borderRadius: 'var(--border-radius-base)'
              }}
            />
            <Input
              placeholder="规则ID"
              value={ruleIdInput}
              onChange={(e) => setRuleIdInput(e.target.value)}
              onPressEnter={() => {
                setSearchText(searchInput);
                setRuleIdFilter(ruleIdInput);
                setClientIdFilter(clientIdInput);
                loadData({ search: searchInput, ruleId: ruleIdInput, clientId: clientIdInput });
              }}
              allowClear
              onClear={() => {
                setRuleIdInput('');
                setRuleIdFilter('');
              }}
              style={{
                width: 120,
                borderRadius: 'var(--border-radius-base)'
              }}
            />
            <Input
              placeholder="客户ID"
              value={clientIdInput}
              onChange={(e) => {
                setClientIdInput(e.target.value);
                setClientIdFilter(e.target.value);
              }}
              onPressEnter={() => {
                setSearchText(searchInput);
                setRuleIdFilter(ruleIdInput);
                setClientIdFilter(clientIdInput);
                loadData({ search: searchInput, ruleId: ruleIdInput, clientId: clientIdInput });
              }}
              allowClear
              onClear={() => {
                setClientIdInput('');
                setClientIdFilter('');
              }}
              style={{
                width: 120,
                borderRadius: 'var(--border-radius-base)'
              }}
            />
            <Button
              type="primary"
              onClick={() => {
                setSearchText(searchInput);
                setRuleIdFilter(ruleIdInput);
                setClientIdFilter(clientIdInput);
                // 搜索时重置到第一页
                loadData({ 
                  search: searchInput, 
                  ruleId: ruleIdInput, 
                  clientId: clientIdInput,
                  page: 1
                });
              }}
              style={{
                borderRadius: 'var(--border-radius-base)'
              }}
            >
              搜索
            </Button>
            <Button
              onClick={() => {
                setSearchInput('');
                setRuleIdInput('');
                setClientIdInput('');
                setSearchText('');
                setRuleIdFilter('');
                setClientIdFilter('');
                // 重置时回到第一页
                loadData({ search: '', ruleId: '', clientId: '', page: 1 });
              }}
              style={{
                borderRadius: 'var(--border-radius-base)'
              }}
            >
              重置
            </Button>
          </Space>
          <div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreateRule}
              style={{
                borderRadius: 'var(--border-radius-base)',
                transition: 'var(--transition-base)',
                height: '38px',
                padding: '0 24px',
                fontWeight: 500,
                boxShadow: '0 2px 0 rgba(99, 102, 241, 0.1)'
              }}
            >
              新建规则
            </Button>
          </div>
        </div>

        <Table
          dataSource={ruleRows}
          rowKey="id"
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: (page, pageSize) => {
              setPagination(prev => ({ ...prev, page, limit: pageSize }));
              loadData({ page, limit: pageSize });
            },
            showTotal: (total) => (
              <span style={{ color: 'var(--color-text-secondary)', fontSize: '13px', fontWeight: 500 }}>
                共 <span style={{ color: 'var(--color-primary)', fontWeight: 600 }}>{total}</span> 条记录
              </span>
            ),
            style: { marginTop: '24px', paddingTop: '16px', borderTop: '1px solid var(--color-border-light)' }
          }}
          columns={columns}
          loading={loading}
          style={{
            borderRadius: 'var(--border-radius-lg)',
            overflow: 'hidden'
          }}
          rowClassName={(record, index) =>
            index % 2 === 0 ? 'table-row-even' : 'table-row-odd'
          }
        />
      </Card>

      {/* 规则名称输入弹窗 */}
      <Modal
        title={
          <div style={{ fontSize: '18px', fontWeight: 600 }}>
            新建规则
          </div>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        okText="保存"
        cancelText="取消"
        okButtonProps={{
          style: {
            borderRadius: 'var(--border-radius-base)',
            fontWeight: 500
          }
        }}
        cancelButtonProps={{
          style: {
            borderRadius: 'var(--border-radius-base)'
          }
        }}
      >
        <Form layout="vertical">
          <Form.Item label="规则名称" required>
            <input
              id="rule-name-input"
              placeholder="请输入规则名称，如：移动端访客拦截规则"
              style={{
                width: '100%',
                height: 32,
                padding: '4px 11px',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--border-radius-base)',
                transition: 'var(--transition-base)',
              }}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 统一的规则配置模态框 */}
      <RuleConfigModal
        visible={ruleConfigModalVisible}
        mode={ruleConfigMode}
        title={creatingNew ? '新建规则' : editingRuleId ? `编辑规则 #${editingRuleId}` : '规则配置'}
        rules={rules}
        ruleId={editingRuleId}
        ruleName={editingRuleId ? ruleRows.find(r => r.id === editingRuleId)?.name : undefined}
        onCancel={() => setRuleConfigModalVisible(false)}
        onSave={async (updatedRules, updatedRuleName) => {
          try {
            const fullRuleConfig: any = {};
            for (const r of updatedRules) {
              fullRuleConfig[r.type] = r.config || {};
            }

            if (creatingNew) {
              // 新建规则
              const effectiveName = (updatedRuleName || '').trim();
              if (!effectiveName) {
                message.error('请填写规则名称');
                return;
              }
              await ruleService.createRule({
                name: effectiveName,
                rule_config: fullRuleConfig
              });
              message.success('规则创建成功');
            } else if (editingRuleId) {
              // 编辑规则
              const updateData: any = {
                id: editingRuleId,
                rule_config: fullRuleConfig
              };

              // 如果规则名称有修改，同时更新名称
              const originalName = ruleRows.find(r => r.id === editingRuleId)?.name || '';
              const effectiveName = (updatedRuleName || originalName || '').trim();
              if (effectiveName && effectiveName !== originalName) {
                updateData.name = effectiveName;
              }

              await ruleService.updateRule(updateData);
              message.success('规则更新成功');
            }

            setRuleConfigModalVisible(false);
            loadData(); // 重新加载数据
          } catch (error) {
            console.error('保存规则失败:', error);
            handleApiError(error, '保存失败');
          }
        }}
      />

      <style>{`
        .table-row-hover:hover {
          background-color: var(--color-bg-tertiary);
          transition: var(--transition-base);
        }
      `}</style>
    </div>
  );
};

export default RuleManagement;
